'use client'

import React from 'react'
import { toast } from 'sonner'
import { useTranslations, useLocale } from 'next-intl'
import { Button } from '@/components/ui/button'

type Props = {
  productId: number | string
}

/**
 * "Notify me when back in stock" signup, shown on out-of-stock product pages.
 * Posts the customer's email to /api/stock-notifications; the product's
 * afterChange hook emails them when inventory returns.
 */
export function BackInStockForm({ productId }: Props) {
  const t = useTranslations('product')
  const locale = useLocale()
  const [email, setEmail] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [done, setDone] = React.useState(false)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || submitting) return

    setSubmitting(true)
    try {
      const res = await fetch('/api/stock-notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, email: email.trim(), locale }),
      })
      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        toast.error(data?.error || t('notifyError'))
        return
      }

      if (data?.alreadyInStock) {
        toast.success(t('notifyAlreadyInStock'))
      } else if (data?.alreadySubscribed) {
        toast.info(t('notifyAlreadySubscribed'))
        setDone(false)
        return
      } else {
        toast.success(t('notifySuccess'))
      }
      setDone(true)
      setEmail('')
    } catch {
      toast.error(t('notifyError'))
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <p className="text-sm text-green-500 font-mono" role="status">
        {t('notifyConfirmed')}
      </p>
    )
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2 w-full">
      <label htmlFor="back-in-stock-email" className="text-sm text-white font-mono">
        {t('notifyPrompt')}
      </label>
      <div className="flex gap-2 w-full">
        <input
          id="back-in-stock-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('notifyEmailPlaceholder')}
          className="flex-1 min-w-0 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/70 focus:outline-none focus:border-white/30"
        />
        <Button
          type="submit"
          variant="neon-white"
          disabled={submitting}
          className="font-mono uppercase tracking-wider rounded-lg whitespace-nowrap dark:bg-black dark:text-[var(--neon-orange)] dark:border-white/10"
        >
          {submitting ? '…' : t('notifyButton')}
        </Button>
      </div>
    </form>
  )
}
